import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useApiClient } from '../utils/apiClient';
import { CenteredAuthCard, StateSuccess, StateError } from '../auth';
import { takeReturnPath } from '../auth/returnPath';

type CallbackStatus = 'processing' | 'success' | 'error';

const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const apiClient = useApiClient(() => null);
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [user, setUser] = useState<{
    avatarUrl?: string;
    firstName?: string;
    lastName?: string;
  } | null>(null);
  const processingRef = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      if (processingRef.current) return;
      processingRef.current = true;

      try {
        const code  = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');

        if (error || !code || !state) {
          setStatus('error');
          processingRef.current = false;
          return;
        }

        let providerId = sessionStorage.getItem('ssoProviderId');

        if (!providerId) {
          await new Promise((resolve) => setTimeout(resolve, 100));
          providerId = sessionStorage.getItem('ssoProviderId');
        }

        if (!providerId) {
          setStatus('error');
          processingRef.current = false;
          return;
        }

        const callbackKey = `sso_callback_${code}_${state}`;
        if (sessionStorage.getItem(callbackKey)) {
          setStatus('error');
          processingRef.current = false;
          return;
        }
        sessionStorage.setItem(callbackKey, 'processing');

        const data = await apiClient.post<{
          accessToken: string;
          user: { avatarUrl?: string; firstName?: string; lastName?: string };
        }>('/api/auth/sso/callback', { code, state, providerId });

        localStorage.setItem('accessToken', data.accessToken);
        sessionStorage.removeItem('ssoProviderId');
        sessionStorage.setItem(callbackKey, 'completed');

        setUser(data.user);
        setStatus('success');

        // Full reload so AuthContext re-reads localStorage
        setTimeout(() => {
          window.location.href = takeReturnPath();
        }, 2000);
      } catch {
        const code  = searchParams.get('code');
        const state = searchParams.get('state');
        if (code && state) {
          sessionStorage.removeItem(`sso_callback_${code}_${state}`);
        }
        setStatus('error');
        processingRef.current = false;
      }
    };

    void handleCallback();
  }, [searchParams, apiClient]);

  if (status === 'success')
    return (
      <StateSuccess
        avatarUrl={user?.avatarUrl}
        name={[user?.firstName, user?.lastName].filter(Boolean).join(' ')}
      />
    );
  if (status === 'error')   return <StateError />;

  return (
    <CenteredAuthCard>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          py: 2,
          textAlign: 'center',
        }}
      >
        <CircularProgress size={40} />
        <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
          Processing authentication…
        </Typography>
      </Box>
    </CenteredAuthCard>
  );
};

export default AuthCallback;
